import readXlsxFile from 'read-excel-file/browser';
import type {
  ImportRow,
  ImportVisit,
  ImportSale,
  ImportRefraction,
  ImportVisualAcuity,
  ImportSaleExtra,
} from '../services/api';

export const IMPORT_SHEET_NAME = 'Pacientes';

// A parsed spreadsheet row, ready for preview and (if valid) submission.
export interface ParsedRow {
  rowNumber: number; // 1-based spreadsheet row number (for the report)
  displayName: string;
  matchKeys: string; // human summary of the email/nit/phone present
  hasVisit: boolean;
  hasSale: boolean;
  visitDate: string;
  errors: string[];
  valid: boolean;
  payload: ImportRow;
}

export interface ParseResult {
  rows: ParsedRow[];
  fatalError?: string; // sheet missing / no header / empty
}

// Normalize a header/value for comparison: trim, lowercase, strip accents.
const norm = (v: unknown): string =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

// Maps normalized row-2 header labels → internal column keys.
const HEADER_KEYS: Record<string, string> = {
  'nombre *': 'first_name',
  nombre: 'first_name',
  apellido: 'last_name',
  telefono: 'phone',
  correo: 'email',
  direccion: 'address',
  nit: 'nit',
  ocupacion: 'occupation',
  edad: 'age',
  genero: 'gender',
  hipertension: 'hypertension',
  diabetes: 'diabetes',
  'antecedentes otros': 'history_other',
  sintomas: 'symptoms',
  diagnostico: 'diagnosis',
  'av od sc': 'av_od_sc',
  'av od cc': 'av_od_cc',
  'av oi sc': 'av_oi_sc',
  'av oi cc': 'av_oi_cc',
  'lens. od esfera': 'lens_od_sphere',
  'lens. od cilindro': 'lens_od_cyl',
  'lens. od eje': 'lens_od_axis',
  'lens. oi esfera': 'lens_oi_sphere',
  'lens. oi cilindro': 'lens_oi_cyl',
  'lens. oi eje': 'lens_oi_axis',
  'rx final od esfera': 'rx_od_sphere',
  'rx final od cilindro': 'rx_od_cyl',
  'rx final od eje': 'rx_od_axis',
  'rx final od add': 'rx_od_add',
  'rx final oi esfera': 'rx_oi_sphere',
  'rx final oi cilindro': 'rx_oi_cyl',
  'rx final oi eje': 'rx_oi_axis',
  'rx final oi add': 'rx_oi_add',
  'armazon descripcion': 'frame_desc',
  'armazon precio': 'frame_price',
  'lente descripcion': 'lens_desc',
  'lente precio': 'lens_price',
  'extra 1 descripcion': 'extra1_desc',
  'extra 1 precio': 'extra1_price',
  'extra 2 descripcion': 'extra2_desc',
  'extra 2 precio': 'extra2_price',
  'extra 3 descripcion': 'extra3_desc',
  'extra 3 precio': 'extra3_price',
  'monto pagado': 'amount_paid',
  'fecha de visita': 'visit_date',
};

type Grid = unknown[][];

// Locate the header row (the one containing "Nombre") within the first few rows.
const findHeaderRowIndex = (grid: Grid): number => {
  for (let i = 0; i < Math.min(grid.length, 6); i++) {
    const row = grid[i] ?? [];
    if (row.some((c) => norm(c).startsWith('nombre'))) {
      return i;
    }
  }
  return -1;
};

// Build { columnKey: columnIndex } from the header row.
const buildColumnMap = (headerRow: unknown[]): Record<string, number> => {
  const map: Record<string, number> = {};
  headerRow.forEach((cell, idx) => {
    const key = HEADER_KEYS[norm(cell)];
    if (key && !(key in map)) {
      map[key] = idx;
    }
  });
  return map;
};

const cell = (row: unknown[], map: Record<string, number>, key: string): string => {
  const idx = map[key];
  if (idx === undefined) return '';
  const v = row[idx];
  if (v instanceof Date) return formatDate(v);
  return String(v ?? '').trim();
};

// Format a Date as "YYYY-MM-DD" using its UTC fields (xlsx parses date-only cells as UTC midnight).
const formatDate = (d: Date): string => {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Parse a decimal number, tolerating comma decimals. Returns null when not a number.
const parseNum = (s: string): number | null => {
  if (s === '') return null;
  const v = parseFloat(s.replace(',', '.'));
  return Number.isFinite(v) ? v : null;
};

// Parse a strict integer. Returns null when not a clean integer.
const parseIntStrict = (s: string): number | null => {
  if (s === '') return null;
  if (!/^-?\d+$/.test(s)) return null;
  return parseInt(s, 10);
};

const GENDER_MAP: Record<string, string> = {
  masculino: 'M',
  femenino: 'F',
  otro: 'O',
  m: 'M',
  f: 'F',
  o: 'O',
};

// Parse the whole workbook (File) into preview-ready rows.
export const parseImportWorkbook = async (file: File): Promise<ParseResult> => {
  let grid: Grid;
  try {
    const sheets = await readXlsxFile(file);
    const sheet = sheets.find((s) => s.sheet === IMPORT_SHEET_NAME) ?? sheets[0];
    if (!sheet) {
      return { rows: [], fatalError: 'El archivo no contiene hojas.' };
    }
    grid = sheet.data as Grid;
  } catch (e) {
    return { rows: [], fatalError: e instanceof Error ? e.message : 'No se pudo leer el archivo.' };
  }

  const headerIdx = findHeaderRowIndex(grid);
  if (headerIdx === -1) {
    return { rows: [], fatalError: 'No se encontró la fila de encabezados (columna "Nombre"). Use la plantilla oficial.' };
  }

  const map = buildColumnMap(grid[headerIdx]);
  if (!('first_name' in map)) {
    return { rows: [], fatalError: 'La plantilla no tiene la columna "Nombre".' };
  }

  const rows: ParsedRow[] = [];

  for (let i = headerIdx + 1; i < grid.length; i++) {
    const raw = grid[i] ?? [];

    // Skip the hint row that ships under the headers (col A === "Requerido").
    if (norm(raw[map['first_name']]).startsWith('requerido')) continue;

    // Skip completely empty rows.
    const isEmpty = raw.every((c) => String(c ?? '').trim() === '');
    if (isEmpty) continue;

    rows.push(parseRow(raw, map, i + 1));
  }

  return { rows };
};

const parseRow = (raw: unknown[], map: Record<string, number>, rowNumber: number): ParsedRow => {
  const errors: string[] = [];
  const get = (key: string) => cell(raw, map, key);

  // ── Patient ────────────────────────────────────────────────────────────────
  const firstName = get('first_name');
  const phone = get('phone');
  const email = get('email');
  const nit = get('nit');

  if (firstName === '') errors.push('Falta el nombre (requerido).');
  if (phone === '' && email === '' && nit === '') {
    errors.push('Falta llave de coincidencia: teléfono, correo o NIT.');
  }

  let age: number | undefined;
  const ageRaw = get('age');
  if (ageRaw !== '') {
    const a = parseIntStrict(ageRaw);
    if (a === null || a < 0 || a > 130) errors.push(`Edad inválida: "${ageRaw}".`);
    else age = a;
  }

  let gender: string | undefined;
  const genderRaw = get('gender');
  if (genderRaw !== '') {
    const g = GENDER_MAP[norm(genderRaw)];
    if (!g) errors.push(`Género inválido: "${genderRaw}" (use Masculino, Femenino u Otro).`);
    else gender = g;
  }

  const parseBool = (key: string, label: string): boolean => {
    const v = norm(get(key));
    if (v === '') return false;
    if (v === 'si' || v === 'sí' || v === 'true' || v === '1') return true;
    if (v === 'no' || v === 'false' || v === '0') return false;
    errors.push(`${label} inválido: "${get(key)}" (use SI o NO).`);
    return false;
  };
  const hypertension = parseBool('hypertension', 'Hipertensión');
  const diabetes = parseBool('diabetes', 'Diabetes');

  // Numeric helper that records an error on bad input but keeps going.
  const num = (key: string, label: string): number | undefined => {
    const s = get(key);
    if (s === '') return undefined;
    const v = parseNum(s);
    if (v === null) {
      errors.push(`${label} no es un número: "${s}".`);
      return undefined;
    }
    return v;
  };
  const axis = (key: string, label: string): number | undefined => {
    const s = get(key);
    if (s === '') return undefined;
    const v = parseIntStrict(s);
    if (v === null || v < 0 || v > 180) {
      errors.push(`${label} inválido: "${s}" (entero 0-180).`);
      return undefined;
    }
    return v;
  };

  // ── Clinical visit ───────────────────────────────────────────────────────────
  const symptoms = get('symptoms');
  const diagnosis = get('diagnosis');

  const va: ImportVisualAcuity = {
    od_distance_sc: get('av_od_sc') || undefined,
    od_distance_cc: get('av_od_cc') || undefined,
    oi_distance_sc: get('av_oi_sc') || undefined,
    oi_distance_cc: get('av_oi_cc') || undefined,
  };
  const hasVA = Object.values(va).some((v) => v !== undefined);

  const lensometry: ImportRefraction = {
    od_sphere_far: num('lens_od_sphere', 'Lens. OD Esfera'),
    od_cylinder_far: num('lens_od_cyl', 'Lens. OD Cilindro'),
    od_axis_far: axis('lens_od_axis', 'Lens. OD Eje'),
    oi_sphere_far: num('lens_oi_sphere', 'Lens. OI Esfera'),
    oi_cylinder_far: num('lens_oi_cyl', 'Lens. OI Cilindro'),
    oi_axis_far: axis('lens_oi_axis', 'Lens. OI Eje'),
  };
  const hasLens = Object.values(lensometry).some((v) => v !== undefined);

  const finalRx: ImportRefraction = {
    od_sphere_far: num('rx_od_sphere', 'Rx Final OD Esfera'),
    od_cylinder_far: num('rx_od_cyl', 'Rx Final OD Cilindro'),
    od_axis_far: axis('rx_od_axis', 'Rx Final OD Eje'),
    od_add_far: num('rx_od_add', 'Rx Final OD Add'),
    oi_sphere_far: num('rx_oi_sphere', 'Rx Final OI Esfera'),
    oi_cylinder_far: num('rx_oi_cyl', 'Rx Final OI Cilindro'),
    oi_axis_far: axis('rx_oi_axis', 'Rx Final OI Eje'),
    oi_add_far: num('rx_oi_add', 'Rx Final OI Add'),
  };
  const hasRx = Object.values(finalRx).some((v) => v !== undefined);

  const hasVisit = symptoms !== '' || diagnosis !== '' || hasVA || hasLens || hasRx;

  let visit: ImportVisit | undefined;
  if (hasVisit) {
    visit = {
      symptoms: symptoms || undefined,
      diagnosis: diagnosis || undefined,
      visual_acuity: hasVA ? va : undefined,
      lensometry: hasLens ? lensometry : undefined,
      final_rx: hasRx ? finalRx : undefined,
    };
  }

  // ── Sale ─────────────────────────────────────────────────────────────────────
  const frameDesc = get('frame_desc');
  const framePrice = num('frame_price', 'Armazón Precio');
  const lensDesc = get('lens_desc');
  const lensPrice = num('lens_price', 'Lente Precio');
  const amountPaid = num('amount_paid', 'Monto Pagado');

  const extras: ImportSaleExtra[] = [];
  for (const n of [1, 2, 3]) {
    const desc = get(`extra${n}_desc`);
    const price = num(`extra${n}_price`, `Extra ${n} Precio`);
    if (desc !== '') {
      extras.push({ description: desc, price: price ?? 0 });
    }
  }

  const hasSale =
    frameDesc !== '' ||
    framePrice !== undefined ||
    lensDesc !== '' ||
    lensPrice !== undefined ||
    extras.length > 0 ||
    amountPaid !== undefined;

  let sale: ImportSale | undefined;
  if (hasSale) {
    sale = {
      frame_description: frameDesc || undefined,
      frame_price: framePrice,
      lens_description: lensDesc || undefined,
      lens_price: lensPrice,
      extras: extras.length > 0 ? extras : undefined,
      amount_paid: amountPaid,
    };
  }

  // ── Visit date ───────────────────────────────────────────────────────────────
  const visitDateRaw = get('visit_date');
  let visitDate = '';
  if (visitDateRaw !== '') {
    if (!DATE_RE.test(visitDateRaw)) {
      errors.push(`Fecha de visita inválida: "${visitDateRaw}" (use el formato AAAA-MM-DD).`);
    } else {
      visitDate = visitDateRaw;
    }
  }

  const matchKeyParts: string[] = [];
  if (phone) matchKeyParts.push(`Tel: ${phone}`);
  if (email) matchKeyParts.push(email);
  if (nit) matchKeyParts.push(`NIT: ${nit}`);

  return {
    rowNumber,
    displayName: [firstName, get('last_name')].filter(Boolean).join(' ') || '(sin nombre)',
    matchKeys: matchKeyParts.join(' · ') || '—',
    hasVisit,
    hasSale,
    visitDate,
    errors,
    valid: errors.length === 0,
    payload: {
      row_number: rowNumber,
      visit_date: visitDate || undefined,
      patient: {
        first_name: firstName,
        last_name: get('last_name') || undefined,
        phone: phone || undefined,
        email: email || undefined,
        address: get('address') || undefined,
        nit: nit || undefined,
        occupation: get('occupation') || undefined,
        age,
        gender,
        history_hypertension: hypertension,
        history_diabetes: diabetes,
        history_other: get('history_other') || undefined,
      },
      visit,
      sale,
    },
  };
};
